/**
 * Shared client-side listing filter (HomePage match count, /search results).
 *
 * - Free-text `query` is parsed for simple price phrases ("under $22k",
 *   "below 30,000") into min/max price, then the remainder is token-matched
 *   against year, make, model, trim, description, body style, drive type,
 *   color, transmission, fuel type, and location (case-insensitive). Filler
 *   words ("under", "near", "miles") and short standalone numbers (e.g. "50"
 *   from "50 miles") are ignored for tokens.
 * - Structured filters (make, model, year/price/mileage ranges, body, color,
 *   transmission, fuel, drive, distance) still apply on top of the query.
 * - Color / body / fuel / drive / transmission matching is canonical so
 *   "Black" hits "Carbon Black Metallic" and "Truck" hits "Pickup".
 */

import { haversineMiles, LatLng } from "./distance";

export interface ListingFilters {
  query?: string;
  make?: string | null;
  model?: string | null;
  minYear?: number | null;
  maxYear?: number | null;
  minPrice?: number | null;
  maxPrice?: number | null;
  minMileage?: number | null;
  maxMileage?: number | null;
  bodyStyle?: string | null;
  color?: string | null;
  transmission?: string | null;
  fuelType?: string | null;
  driveType?: string | null;
  userLocation?: LatLng | null;
  /** Miles from search ZIP; null = no distance filter. */
  distanceMaxMiles?: number | null;
}

/** Canonical body styles shown in the search sidebar. */
export const BODY_STYLE_OPTIONS = [
  "SUV",
  "Sedan",
  "Truck",
  "Coupe",
  "Hatchback",
  "Van",
  "Wagon",
  "Convertible",
] as const;

/** Canonical drive types shown in the search sidebar. */
export const DRIVE_TYPE_OPTIONS = ["AWD", "4WD", "FWD", "RWD"] as const;

/** Canonical transmissions shown in the search sidebar. */
export const TRANSMISSION_OPTIONS = ["Automatic", "Manual", "CVT"] as const;

/** Canonical fuel types shown in the search sidebar. */
export const FUEL_TYPE_OPTIONS = [
  "Gasoline",
  "Diesel",
  "Hybrid",
  "Electric",
] as const;

export const tokenizeQuery = (q: string): string[] =>
  q.toLowerCase().split(/[\s,]+/).map(t => t.trim()).filter(Boolean);

/** Filler words removed from token matching (price phrases are stripped separately). */
const STOP_TOKENS = new Set([
  "under",
  "below",
  "above",
  "over",
  "than",
  "less",
  "more",
  "up",
  "to",
  "at",
  "most",
  "least",
  "with",
  "the",
  "a",
  "an",
  "for",
  "and",
  "or",
  "of",
  "in",
  "within",
  "miles",
  "mile",
  "near",
  "zip",
]);

function parseMoneyToken(num: string, k?: string): number | null {
  const cleaned = num.replace(/,/g, "").trim();
  if (!cleaned) return null;
  const mult = k?.toLowerCase() === "k" ? 1000 : 1;
  const n = parseFloat(cleaned);
  if (!Number.isFinite(n)) return null;
  return n * mult;
}

/**
 * Pulls simple price intent from free text so "under $22,000" / "under 22k"
 * becomes maxPrice while the rest of the string still drives token matching.
 */
export function parseFreeTextQuery(raw: string): {
  residualQuery: string;
  minPrice: number | null;
  maxPrice: number | null;
} {
  let s = raw.trim();
  let minPrice: number | null = null;
  let maxPrice: number | null = null;

  const applyMax = (v: number) => {
    maxPrice = maxPrice == null ? v : Math.min(maxPrice, v);
  };
  const applyMin = (v: number) => {
    minPrice = minPrice == null ? v : Math.max(minPrice, v);
  };

  const reMax =
    /\b(?:under|below|less\s+than|up\s+to|at\s+most)\s+\$?\s*([\d,]+(?:\.\d+)?)(k)?\b/gi;
  s = s.replace(reMax, (_m, num: string, k?: string) => {
    const v = parseMoneyToken(num, k);
    if (v != null) applyMax(v);
    return " ";
  });

  const reMin =
    /\b(?:over|above|more\s+than|at\s+least)\s+\$?\s*([\d,]+(?:\.\d+)?)(k)?\b/gi;
  s = s.replace(reMin, (_m, num: string, k?: string) => {
    const v = parseMoneyToken(num, k);
    if (v != null) applyMin(v);
    return " ";
  });

  s = s.replace(/\s+/g, " ").trim();
  return { residualQuery: s, minPrice, maxPrice };
}

export function expandListingFilters(f: ListingFilters): ListingFilters {
  if (!f.query?.trim()) return f;
  const parsed = parseFreeTextQuery(f.query);
  const residual = parsed.residualQuery.trim();
  return {
    ...f,
    query: residual ? residual : undefined,
    minPrice: f.minPrice ?? parsed.minPrice,
    maxPrice: f.maxPrice ?? parsed.maxPrice,
  };
}

function tokenMatchesHaystack(token: string, haystack: string): boolean {
  if (haystack.includes(token)) return true;
  if (token.length >= 4 && token.endsWith("s")) {
    const singular = token.slice(0, -1);
    if (haystack.includes(singular)) return true;
  }
  if (!token.endsWith("s") && token.length >= 3) {
    if (haystack.includes(`${token}s`)) return true;
  }
  return false;
}

/**
 * Minimum shape this filter cares about on a listing.
 * Kept structural so generated GraphQL row types satisfy it without casts.
 */
export interface FilterableListing {
  year?: number | null;
  make?: string | null;
  model?: string | null;
  trim?: string | null;
  description?: string | null;
  bodyStyle?: string | null;
  driveType?: string | null;
  transmission?: string | null;
  fuelType?: string | null;
  exteriorColor?: string | null;
  locationAddress?: string | null;
  price?: number | string | { toString(): string } | null;
  mileage?: number | string | { toString(): string } | null;
  latitude?: number | string | { toString(): string } | null;
  longitude?: number | string | { toString(): string } | null;
}

const toFloat = (v: unknown): number | null => {
  if (v == null) return null;
  const n = typeof v === "number" ? v : parseFloat(String(v));
  return Number.isFinite(n) ? n : null;
};

const BODY_ALIASES: Record<string, string> = {
  pickup: "Truck",
  "pickup truck": "Truck",
  truck: "Truck",
  suv: "SUV",
  "sport utility": "SUV",
  "sport utility vehicle": "SUV",
  crossover: "SUV",
  sedan: "Sedan",
  coupe: "Coupe",
  hatchback: "Hatchback",
  hatch: "Hatchback",
  van: "Van",
  minivan: "Van",
  wagon: "Wagon",
  convertible: "Convertible",
};

/** Map a listing or filter body string onto a canonical body style. */
export function canonicalizeBodyStyle(raw?: string | null): string | null {
  if (!raw?.trim()) return null;
  const key = raw.trim().toLowerCase();
  return BODY_ALIASES[key] || raw.trim();
}

export function bodyStyleMatches(
  listing?: string | null,
  filter?: string | null
): boolean {
  if (!filter) return true;
  const a = canonicalizeBodyStyle(listing);
  const b = canonicalizeBodyStyle(filter);
  if (!a || !b) return false;
  return a.toLowerCase() === b.toLowerCase();
}

/** Map provider fuel strings onto Gasoline / Diesel / Hybrid / Electric. */
export function canonicalizeFuelType(raw?: string | null): string | null {
  if (!raw?.trim()) return null;
  const s = raw.toLowerCase();
  const hasElectric = /\belectric\b/.test(s);
  const hasIce = /(unleaded|gasoline|petrol|\bgas\b|diesel|hybrid|phev|plugin|plug-in)/.test(s);
  if (hasElectric && hasIce) return "Hybrid";
  if (hasElectric) return "Electric";
  if (/hybrid|phev|plugin|plug-in/.test(s)) return "Hybrid";
  if (/diesel/.test(s)) return "Diesel";
  if (/unleaded|gasoline|petrol|\bgas\b/.test(s)) return "Gasoline";
  return raw.trim();
}

export function fuelTypeMatches(
  listing?: string | null,
  filter?: string | null
): boolean {
  if (!filter) return true;
  const a = canonicalizeFuelType(listing);
  const b = canonicalizeFuelType(filter);
  if (!a || !b) return false;
  return a.toLowerCase() === b.toLowerCase();
}

/** Map drivetrain strings onto AWD / 4WD / FWD / RWD. */
export function canonicalizeDriveType(raw?: string | null): string | null {
  if (!raw?.trim()) return null;
  const s = raw.toLowerCase();
  if (/\b(awd|all[-\s]?wheel)\b/.test(s) || /quattro|4matic|xdrive/.test(s)) return "AWD";
  if (/\b(4wd|4x4|four[-\s]?wheel)\b/.test(s)) return "4WD";
  if (/\b(fwd|front[-\s]?wheel)\b/.test(s)) return "FWD";
  if (/\b(rwd|rear[-\s]?wheel)\b/.test(s)) return "RWD";
  return raw.trim();
}

export function driveTypeMatches(
  listing?: string | null,
  filter?: string | null
): boolean {
  if (!filter) return true;
  const a = canonicalizeDriveType(listing);
  const b = canonicalizeDriveType(filter);
  if (!a || !b) return false;
  return a.toLowerCase() === b.toLowerCase();
}

/** Map transmission strings onto Automatic / Manual / CVT. */
export function canonicalizeTransmission(raw?: string | null): string | null {
  if (!raw?.trim()) return null;
  const s = raw.toLowerCase();
  if (/\bcvt\b/.test(s)) return "CVT";
  if (/manual|\bmt\b/.test(s)) return "Manual";
  // PDK / single-speed EV "Electric" are automatics for sidebar filtering.
  if (/automatic|tiptronic|\bat\b|dct|dsg|\bpdk\b|single[-\s]?speed/.test(s)) {
    return "Automatic";
  }
  if (/^electric$/.test(s.trim())) return "Automatic";
  return raw.trim();
}

export function transmissionMatches(
  listing?: string | null,
  filter?: string | null
): boolean {
  if (!filter) return true;
  const a = canonicalizeTransmission(listing);
  const b = canonicalizeTransmission(filter);
  if (!a || !b) return false;
  return a.toLowerCase() === b.toLowerCase();
}

function escapeRegExp(s: string): string {
  return s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

/**
 * Family match: filter "Black" hits "Carbon Black Metallic" and "Navy Blue"
 * hits "Blue", but "Red" does not hit "Infrared".
 */
export function colorMatches(
  listingColor?: string | null,
  filterColor?: string | null
): boolean {
  if (!filterColor?.trim()) return true;
  if (!listingColor?.trim()) return false;
  const listing = listingColor.trim().toLowerCase();
  const filter = filterColor.trim().toLowerCase();
  if (listing === filter) return true;
  const re = new RegExp(`\\b${escapeRegExp(filter)}\\b`, "i");
  return re.test(listingColor);
}

export function matchesListing(car: FilterableListing, f: ListingFilters): boolean {
  const rawTokens = f.query ? tokenizeQuery(f.query) : [];
  const tokens = rawTokens.filter(
    (t) => !STOP_TOKENS.has(t) && !/^\d{1,3}$/.test(t)
  );
  if (tokens.length > 0) {
    const haystack = [
      car.year?.toString(),
      car.make,
      car.model,
      car.trim,
      car.description,
      car.bodyStyle,
      car.driveType,
      car.transmission,
      car.fuelType,
      car.exteriorColor,
      car.locationAddress,
    ]
      .filter(Boolean)
      .join(" ")
      .toLowerCase();
    if (!tokens.every((t) => tokenMatchesHaystack(t, haystack))) return false;
  }

  if (f.make && car.make?.toLowerCase() !== f.make.toLowerCase()) return false;
  if (f.model && car.model?.toLowerCase() !== f.model.toLowerCase()) return false;

  const year = car.year ?? 0;
  if (f.minYear != null && year < f.minYear) return false;
  if (f.maxYear != null && year > f.maxYear) return false;

  const price = toFloat(car.price) ?? 0;
  if (f.minPrice != null && price < f.minPrice) return false;
  if (f.maxPrice != null && price > f.maxPrice) return false;

  const mileage = toFloat(car.mileage);
  if (f.minMileage != null) {
    if (mileage == null || mileage < f.minMileage) return false;
  }
  if (f.maxMileage != null) {
    if (mileage == null || mileage > f.maxMileage) return false;
  }

  if (f.bodyStyle && !bodyStyleMatches(car.bodyStyle, f.bodyStyle)) return false;
  if (f.color && !colorMatches(car.exteriorColor, f.color)) return false;
  if (f.transmission && !transmissionMatches(car.transmission, f.transmission)) {
    return false;
  }
  if (f.fuelType && !fuelTypeMatches(car.fuelType, f.fuelType)) return false;
  if (f.driveType && !driveTypeMatches(car.driveType, f.driveType)) return false;

  const maxM = f.distanceMaxMiles;
  const hasMax = maxM != null && Number.isFinite(maxM);
  if (f.userLocation && hasMax) {
    const lat = toFloat(car.latitude);
    const lng = toFloat(car.longitude);
    if (lat == null || lng == null) return false;
    const miles = haversineMiles(f.userLocation, { latitude: lat, longitude: lng });
    if (maxM != null && miles > maxM) return false;
  }

  return true;
}

export function applyFilters<T extends FilterableListing>(cars: T[], f: ListingFilters): T[] {
  const effective = expandListingFilters(f);
  return cars.filter((car) => matchesListing(car, effective));
}
